import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import Delete from '../Delete';
import { CustomAccordion, Text } from '../../shared/uiComponents';
import { Loader, InfoRow as Row } from '../StyledComponents';
import { errorNormalizer } from '../../shared/Helpers/functions';

import { DispatchProperties, useSelector } from '../../redux/store';
import { UserProperties } from '../../redux/API/userAPIProperties';
import { dismissClient, getUserClients } from '../../redux/State/clientSlice/userClientSlice';
import { ClientUserProperties } from '../../redux/API/ClientAPIHelpers/userClientProperties';
import { InfoClientProperties } from '../../redux/API/ClientAPIHelpers/clientProperties';
import { AdminTypes } from '../Menu';

const ListItem = ({
	client,
	index,
	userId,
	userRole,
}: {
	client: InfoClientProperties;
	index: number;
	userId: string;
	userRole: number;
}) => {
	const dispatch = useDispatch<DispatchProperties>();

	const fetchUserClients = useCallback(() => {
		if (!userId) return;
		dispatch(getUserClients({ userId, page: '1', pageSize: '8' }));
	}, [userId, dispatch]);

	const dismissHandler = ({ clientId, userId: dismissUserId }: ClientUserProperties) => {
		dispatch(dismissClient({ clientId, userId: dismissUserId }))
			.then(unwrapResult)
			.then(() => {
				toast('Removed');
				fetchUserClients();
			})
			.catch(errorNormalizer);
	};

	const isRemoveAllowed = AdminTypes.cs === userRole ? client.isDirectClient : client.id && userId;

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
			}}
			key={index}
		>
			<Text title={`${index + 1}. ${client.fullName}`} className="marginRight8" type="psm" />
			{isRemoveAllowed && (
				<Delete
					deleteHandler={() =>
						dismissHandler({
							clientId: client.id,
							userId,
						})
					}
				/>
			)}
		</div>
	);
};

const ClientsList = ({ item }: { item: UserProperties }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { id } = item;

	const [clientsPageSize, setClientsPageSize] = useState<number>(8);

	const clients = useSelector((state) => state.userClient.userClients);
	const loading = useSelector((state) => state.userClient.loadingGetUserClients);

	useEffect(() => {
		if (!id) return;
		dispatch(
			getUserClients({
				userId: id,
				page: '1',
				pageSize: clientsPageSize.toString(),
			}),
		);
	}, [id, clientsPageSize, dispatch]);

	return (
		<CustomAccordion
			headerStyle={{ padding: 0 }}
			HeaderContent={() => (
				<Row>
					<Text title="Clients Assigned: " type="psm" fontWeight="semiBold" />
					<Text title={`${clients.totalNumberOfItems}`} />
				</Row>
			)}
			Content={() => (
				<div style={{ padding: '0px 8px' }}>
					{clients.query && clients.query.length > 0 && !loading ? (
						<>
							{clients.query.map((client, index) => (
								<ListItem index={index} client={client} userId={id} userRole={item.role.section.id} key={id} />
							))}
							{clients.hasNextPage && (
								<div style={{ justifyContent: 'center', display: 'flex' }}>
									<Text
										title="Load More"
										type="psm"
										fontWeight="semiBold"
										onClick={() => setClientsPageSize((prev) => prev + 8)}
									/>
								</div>
							)}
						</>
					) : (
						<Text title="None" type="psm" />
					)}
					{loading && <Loader />}
				</div>
			)}
		/>
	);
};

export default ClientsList;
